<template>
    <nav v-if="elements.length" class="pagination is-left">
        <template v-if="pages.last_page !== 1 && type === 'simple'">
            <button class="pagination-previous button" :disabled="disabledPrev" @click="previous">
                <img src="/images/app/svg/arrow-left.svg" alt="" />
            </button>
            <ul class="pagination-list">
                <component
                    :is="element.type"
                    v-for="(element, index) in elements"
                    :key="index"
                    :pageNumber="element.page"
                    :page="pages"
                    :context="context"
                    @update-page="goToPage"
                />
            </ul>
            <button
                v-if="size > 1"
                class="pagination-next button"
                :disabled="disabledNext"
                @click="next"
            >
                <img src="/images/app/svg/arrow-right.svg" alt="" />
            </button>
        </template>

        <template v-else-if="pages.last_page !== 1 && type === 'multy'">
            <div class="pagination-multy">
                <button
                    v-if="size > 1"
                    class="pagination-previous button"
                    :disabled="disabledPrev"
                    @click="previous"
                >
                    <img src="/images/app/svg/arrow-left.svg" alt="" />
                </button>
                <ul v-if="!paginationDisabled" class="pagination-list">
                    <component
                        :is="element.type"
                        v-for="(element, index) in elements"
                        :key="index"
                        :pageNumber="element.page"
                        :page="pages"
                        :context="context"
                        @update-page="goToPage"
                    />
                </ul>
                <button
                    v-if="size > 1"
                    class="pagination-next button"
                    :disabled="disabledNext"
                    @click="next"
                >
                    <img src="/images/app/svg/arrow-right.svg" alt="" />
                </button>
            </div>
        </template>

        <template v-else-if="pages.last_page !== 1">
            <button class="pagination-previous button" :disabled="disabledPrev" @click="previous">
                &larr; Previous
            </button>
            <button
                v-if="size > 1"
                class="pagination-next button"
                :disabled="disabledNext"
                @click="next"
            >
                Next &rarr;
            </button>
            <ul class="pagination-list">
                <component
                    :is="element.type"
                    v-for="(element, index) in elements"
                    :key="index"
                    :pageNumber="element.page"
                    :page="pages"
                    :context="context"
                    @update-page="goToPage"
                />
            </ul>
        </template>

        <length-selector
            v-if="!lengthSelectorDisabled"
            :length="pages.per_page"
            :context="context"
        />
    </nav>
</template>

<script>
import Page from './Pagination/Page.vue';
import Ellipses from './Pagination/Ellipses';
import LengthSelector from './Pagination/LengthSelector';

export default {
    emits: ['updatePage'],
    
    components: {
        Page,
        Ellipses,
        LengthSelector,
    },

    props: {
        pages: { type: Object, required: true },
        context: { type: String, default: '' },
        type: { type: String, default: '' },
        lengthSelectorDisabled: { type: Boolean, default: false },
        lastPageDisabled: { type: Boolean, default: false },
        firstPageDisabled: { type: Boolean, default: false },
        paginationDisabled: { type: Boolean, default: false },
        maxPages: { type: Number, default: 0 },
    },

    data() {
        return {
            elements: [],
            step: 1,
        };
    },

    computed: {
        disabledPrev() {
            return this.pages.current_page === 1;
        },

        disabledNext() {
            if (this.pages.type === 'integrations') return !this.pages.has_more;

            return this.pages.current_page === this.size;
        },

        size() {
            return Math.ceil(this.pages.total / this.pages.per_page);
        },

        current() {
            return this.pages.current_page;
        },
    },

    watch: {
        current() {
            this.paginate();
        },

        pages() {
            this.paginate();
        },

        'pages.per_page'() {
            this.paginate();
        },

        'pages.total'() {
            this.paginate();
        },
    },

    methods: {
        add(start, finish) {
            for (let i = start; i <= finish; i++) {
                this.elements.push({ type: 'page', page: i });
            }
        },

        first() {
            if (!this.firstPageDisabled) return;

            // Add first page with separator
            this.elements.push({ type: 'page', page: 1 }, { type: 'ellipses' });
        },

        last() {
            if (!this.lastPageDisabled) return;

            // Add last page with separator
            this.elements.push({ type: 'ellipses' }, { type: 'page', page: this.size });
        },

        paginate() {
            this.elements = [];

            if (this.size > 1 && this.maxPages) {
                this.first();

                let maxPages = this.size >= this.maxPages ? this.maxPages : this.size;
                let start = this.current;
                let finish = maxPages;
                let startMax = Math.round(maxPages / 2);
                let currentSize = maxPages - startMax + this.current;
                let hasMore = currentSize <= this.size;

                if (this.current <= startMax) {
                    start = 1;
                    finish = maxPages;
                } else if (this.current > startMax) {
                    let resultStartCaseOne = this.current + 1 - startMax;
                    let hasSizeMaxPagesEquals = this.size === maxPages;
                    let resultSizeMaxPages = this.size + 1 - maxPages;

                    start = hasMore
                        ? resultStartCaseOne
                        : hasSizeMaxPagesEquals
                            ? 1
                            : resultSizeMaxPages;
                    finish = hasMore ? currentSize : this.size;
                }

                this.add(start, finish);
                this.last();

                return;
            }

            if (this.size < this.step * 2 + 6) {
                // Case without any ellipse breaks
                this.add(1, this.size);
            } else if (this.current < this.step * 2 + 1) {
                // Case with ellipse breaks at end
                this.add(1, this.step * 2 + 1);
                this.last();
            } else if (this.current > this.size - this.step * 2) {
                // Case with ellipse breaks at beginning
                this.first();
                this.add(this.size - this.step * 2 - 1, this.size);
            } else {
                // Case with ellipse breaks at beginning and end
                this.first();
                this.add(this.current - this.step, this.current + this.step);
                this.last();
            }
        },

        previous() {
            if (this.disabledPrev) return;

            let pageNumber = this.pages.current_page - 1;

            if (this.pages.type === 'integrations') {
                let pageIndex = this.pages.pageIndex - 1;

                pageNumber = this.pages.pages[pageIndex];
            }

            this.goToPage(pageNumber);
        },

        next() {
            if (this.disabledNext) return;

            let pageNumber = this.pages.current_page + 1;

            if (this.pages.type === 'integrations') {
                let pageIndex = this.pages.pageIndex + 1;

                pageNumber = this.pages.pages[pageIndex];
            }

            this.goToPage(pageNumber);
        },

        goToPage(pageNumber) {
            if (this.context) {
                this.$store.dispatch(`${this.context}UpdatePage`, pageNumber);
            } else {
                this.$emit('updatePage', pageNumber);
            }
        },
    },

    mounted() {
        this.paginate();
    },
};
</script>
